import { useCallback, useEffect, useRef, useState } from "react";
import type { RealtimeEvent } from "./types";

export type ChannelStatus =
  | { kind: "disconnected" }
  | { kind: "connecting" }
  | { kind: "connected" }
  | { kind: "error"; code?: number; reason: string };

export interface ConnectParams {
  wsUrl: string;
  token: string;
  leadId?: string;
}

export interface ApiChannelHandle {
  status: ChannelStatus;
  sessionId: string | null;
  leadId: string | null;
  connect: (params: ConnectParams) => void;
  disconnect: () => void;
  send: (text: string) => void;
}

export function useApiChannel(
  onEvent: (event: RealtimeEvent) => void,
): ApiChannelHandle {
  const [status, setStatus] = useState<ChannelStatus>({ kind: "disconnected" });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [leadId, setLeadId] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const disconnect = useCallback(() => {
    const existing = socketRef.current;
    socketRef.current = null;
    if (existing && existing.readyState !== WebSocket.CLOSED) {
      existing.close(1000, "client_disconnect");
    }
    setStatus({ kind: "disconnected" });
  }, []);

  const connect = useCallback(
    ({ wsUrl, token, leadId: resumeId }: ConnectParams) => {
      const prior = socketRef.current;
      socketRef.current = null;
      if (prior && prior.readyState !== WebSocket.CLOSED) {
        prior.close(1000, "client_reconnect");
      }

      setSessionId(null);
      setLeadId(resumeId ?? null);
      setStatus({ kind: "connecting" });

      let url: URL;
      try {
        url = new URL(wsUrl);
      } catch {
        setStatus({ kind: "error", reason: "invalid_url" });
        return;
      }
      url.searchParams.set("token", token);
      if (resumeId) url.searchParams.set("leadId", resumeId);

      let ws: WebSocket;
      try {
        ws = new WebSocket(url.toString());
      } catch (err) {
        setStatus({
          kind: "error",
          reason: err instanceof Error ? err.message : "open_failed",
        });
        return;
      }

      socketRef.current = ws;

      ws.addEventListener("open", () => {
        if (socketRef.current !== ws) return;
        setStatus({ kind: "connected" });
      });

      ws.addEventListener("message", (e) => {
        if (socketRef.current !== ws) return;
        let parsed: RealtimeEvent;
        try {
          parsed = JSON.parse(e.data as string) as RealtimeEvent;
        } catch {
          return;
        }
        if (parsed.type === "agent_start") setSessionId(parsed.sessionId);
        if (
          "leadId" in parsed &&
          typeof (parsed as { leadId?: unknown }).leadId === "string"
        ) {
          setLeadId((parsed as { leadId: string }).leadId);
        }
        onEventRef.current(parsed);
      });

      ws.addEventListener("close", (e) => {
        // A close belonging to a superseded socket must not stomp
        // the current connection's status.
        if (socketRef.current !== ws) return;
        socketRef.current = null;
        const auth = e.code === 4401 || e.code === 4403;
        setStatus(
          auth
            ? { kind: "error", code: e.code, reason: e.reason || "auth_failure" }
            : { kind: "disconnected" },
        );
      });

      ws.addEventListener("error", () => {
        // Browsers omit error detail for security; rely on the
        // follow-up close event for the code+reason.
      });
    },
    [],
  );

  const send = useCallback((text: string) => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "user_message", text }));
  }, []);

  useEffect(() => {
    return () => {
      const ws = socketRef.current;
      socketRef.current = null;
      if (ws && ws.readyState !== WebSocket.CLOSED) {
        ws.close(1000, "unmount");
      }
    };
  }, []);

  return { status, sessionId, leadId, connect, disconnect, send };
}
